// frontend/src/components/calendar/CalendarExceptionsSection.jsx
//
// One-off closures — one row per specific date at this calendar.
// Outlook-style list with inline "+ Add" form. Name is optional.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  IconButton,
  TextField,
} from '@mui/material';
import { ICON, Plus, X } from '../../icons';
import AppDatePicker from '../common/AppDatePicker';

import {
  useCalendarExceptions,
  useCreateCalendarException,
  useDeleteCalendarException,
} from '../../hooks/useCalendarData';
import { translateRequestError } from '../../queries/api';
import { useFormatters } from '../../hooks/useFormatters';
import { useStoreToday } from '../../hooks/useStoreToday';
import '../../styles/calendar.css';
import { showToast } from '../../components/common/appToastBus';


// A NEW calendar exception is a row on the store's operating calendar, so its
// date is seeded from the store's day (useStoreToday), never the browser's.


const CalendarExceptionsSection = ({ calendar }) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const calendarId = calendar?.calendar_id;

  const { exceptions } = useCalendarExceptions(calendarId);
  const createException = useCreateCalendarException(calendarId);
  const deleteException = useDeleteCalendarException();

  // '' until the store's zone lands, so the field starts empty rather than
  // pre-filled with the viewer's day; saveException already refuses an empty
  // date, so nothing can be booked off a guess.
  const storeToday = useStoreToday();

  const [adding, setAdding] = useState(false);
  const [draftDate, setDraftDate] = useState(storeToday);
  const [draftName, setDraftName] = useState('');

  const startAdding = () => {
    setDraftDate(storeToday);
    setDraftName('');
    setAdding(true);
  };
  const cancelAdding = () => setAdding(false);

  const saveException = async () => {
    if (!draftDate) return;
    try {
      await createException.mutateAsync({
        exceptionDate: draftDate,
        exceptionName: draftName.trim(),
      });
      setAdding(false);
    } catch (err) {
      console.error('createCalendarException:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    }
  };

  const removeException = async (id) => {
    try {
      await deleteException.mutateAsync({ id });
    } catch (err) {
      console.error('deleteCalendarException:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    }
  };

  const sortedExceptions = useMemo(
    () => [...exceptions].sort((a, b) => (a.exception_date > b.exception_date ? 1 : -1)),
    [exceptions],
  );

  return (
    <div className="calendar-overrides">
      <div className="calendar-overrides-header">
        <span className="calendar-overrides-count">
          {t('calendar.exceptions.count', { count: exceptions.length })}
        </span>
        {!adding && (
          <Button
            size="small"
            startIcon={<Plus size={ICON.md} />}
            onClick={startAdding}
          >
            {t('calendar.exceptions.add')}
          </Button>
        )}
      </div>

      {sortedExceptions.length === 0 && !adding && (
        <div className="calendar-overrides-empty">
          {t('calendar.exceptions.empty')}
        </div>
      )}

      <ul className="calendar-overrides-list">
        {sortedExceptions.map((e) => (
          <li key={e.calendar_exception_id} className="calendar-overrides-row">
            <span className="calendar-overrides-date">
              {formatDate(e.exception_date)}
            </span>
            <span className="calendar-overrides-name">
              {e.exception_name || <span className="calendar-overrides-unnamed">—</span>}
            </span>
            <IconButton
              size="small"
              onClick={() => removeException(e.calendar_exception_id)}
              aria-label={t('btn.remove')}
            >
              <X size={ICON.sm} />
            </IconButton>
          </li>
        ))}
      </ul>

      {adding && (
        <div className="calendar-overrides-form">
          <AppDatePicker
            label={t('calendar.exceptions.date')}
            value={draftDate}
            onChange={setDraftDate}
          />
          <TextField
            size="small"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder={t('calendar.exceptions.namePlaceholder')}
            label={t('calendar.exceptions.name')}
            sx={{ flex: 1 }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={saveException}
            loading={createException.isPending}
          >
            {t('btn.add')}
          </Button>
          <Button size="small" onClick={cancelAdding}>
            {t('btn.cancel')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CalendarExceptionsSection;
